// useMicLevel — experimental microphone input level for visualization only.
// Clearly not pitch detection. Cleans up the mic on stop/unmount.

import { useCallback, useEffect, useRef, useState } from "react";
import { MicLevelReader } from "@/services/AudioDetector";

export function useMicLevel() {
  const readerRef = useRef<MicLevelReader | null>(null);
  const rafRef = useRef<number | null>(null);
  const [active, setActive] = useState(false);
  const [level, setLevel] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const stop = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    readerRef.current?.stop();
    readerRef.current = null;
    setActive(false);
    setLevel(0);
  }, []);

  const start = useCallback(async () => {
    setError(null);
    try {
      const reader = new MicLevelReader();
      await reader.start();
      readerRef.current = reader;
      setActive(true);
      const tick = () => {
        setLevel(reader.level());
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch (e: any) {
      setError("Microphone unavailable or permission denied");
      setActive(false);
    }
  }, []);

  useEffect(() => () => stop(), [stop]);

  return { active, level, error, start, stop };
}
